'use client';

import * as React from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { JobCategory, JobType } from '@/components/jobs/JobFilters';

const LOCATION_DEBOUNCE_MS = 300;

interface JobFilterUrlState {
  jobType: JobType;
  category: JobCategory;
  location: string;
  debouncedLocation: string;
  rankId: string;
  vesselTypeId: string;
  shorePositionId: string;
  page: number;
  setJobType: (type: JobType) => void;
  setCategory: (category: JobCategory) => void;
  setLocation: (location: string) => void;
  setRankId: (rankId: string) => void;
  setVesselTypeId: (vesselTypeId: string) => void;
  setShorePositionId: (shorePositionId: string) => void;
  setPage: (page: number) => void;
}

type JobFilterUrlUpdates = Partial<{
  jobType: JobType;
  category: JobCategory;
  location: string;
  rankId: string;
  vesselTypeId: string;
  shorePositionId: string;
  page: number;
}>;

export function buildMergedJobFilterParams(
  currentQuery: string,
  updates: JobFilterUrlUpdates,
  pageSize: number
) {
  const params = new URLSearchParams(currentQuery);

  const setOrDelete = (key: string, value: string | number | null | undefined) => {
    const nextValue = value === null || value === undefined ? '' : String(value).trim();
    if (nextValue) params.set(key, nextValue);
    else params.delete(key);
  };

  if ('rankId' in updates) setOrDelete('rank_id', updates.rankId);
  if ('vesselTypeId' in updates) setOrDelete('vessel_type_id', updates.vesselTypeId);
  if ('shorePositionId' in updates) setOrDelete('shore_position_id', updates.shorePositionId);
  if ('category' in updates) {
    if (updates.category === 'training') params.set('category', updates.category);
    else params.delete('category');
  }
  if ('location' in updates) setOrDelete('location', updates.location);
  if ('page' in updates) {
    if ((updates.page ?? 1) > 1) params.set('page', String(updates.page));
    else params.delete('page');
  }
  if ('jobType' in updates) {
    params.delete('page');
  }

  params.delete('page_size');
  if (params.toString()) params.set('page_size', String(pageSize));

  return params;
}

export function buildJobFilterUrl(
  pathname: string,
  currentQuery: string,
  updates: JobFilterUrlUpdates,
  pageSize: number
) {
  const params = buildMergedJobFilterParams(currentQuery, updates, pageSize);
  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

function readPage(searchParams: URLSearchParams) {
  const rawPage = Number(searchParams.get('page') ?? '1');
  return Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
}

function inferJobType(searchParams: URLSearchParams): JobType {
  return searchParams.has('shore_position_id') || searchParams.get('category') === 'training'
    ? 'shore'
    : 'onboard';
}

function useDebouncedValue<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
}

export function useJobFilterUrlState(pageSize: number): JobFilterUrlState {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchParamKey = searchParams.toString();
  const currentParams = React.useMemo(
    () => new URLSearchParams(searchParamKey),
    [searchParamKey]
  );
  const [jobType, setJobTypeState] = React.useState<JobType>(() => inferJobType(searchParams));
  const [category, setCategoryState] = React.useState<JobCategory>(() =>
    searchParams.get('category') === 'training' ? 'training' : 'all'
  );
  const [location, setLocationState] = React.useState(() => searchParams.get('location') ?? '');
  const [rankId, setRankIdState] = React.useState(() => searchParams.get('rank_id') ?? '');
  const [vesselTypeId, setVesselTypeIdState] = React.useState(() => searchParams.get('vessel_type_id') ?? '');
  const [shorePositionId, setShorePositionIdState] = React.useState(() => searchParams.get('shore_position_id') ?? '');
  const [page, setPageState] = React.useState(() => readPage(searchParams));
  const debouncedLocation = useDebouncedValue(location, LOCATION_DEBOUNCE_MS);
  const syncedLocationRef = React.useRef<string | null>(null);

  const writeUrl = React.useCallback((updates: JobFilterUrlUpdates, mode: 'push' | 'replace') => {
    const currentQuery = typeof window === 'undefined' ? searchParamKey : window.location.search;
    const nextUrl = buildJobFilterUrl(pathname, currentQuery, updates, pageSize);
    // Next App Router swallowed these query-only writes in production builds
    // while still updating filter state. Native history keeps shareable URLs in sync.
    if (mode === 'push') {
      window.history.pushState(window.history.state, '', nextUrl);
    } else {
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [pageSize, pathname, searchParamKey]);

  const lastDebouncedLocationRef = React.useRef(debouncedLocation);

  React.useEffect(() => {
    const nextJobType = inferJobType(currentParams);
    const nextLocation = currentParams.get('location') ?? '';
    syncedLocationRef.current = nextLocation;
    lastDebouncedLocationRef.current = nextLocation;
    setJobTypeState(nextJobType);
    setCategoryState(currentParams.get('category') === 'training' ? 'training' : 'all');
    setLocationState(nextLocation);
    setRankIdState(currentParams.get('rank_id') ?? '');
    setVesselTypeIdState(currentParams.get('vessel_type_id') ?? '');
    setShorePositionIdState(currentParams.get('shore_position_id') ?? '');
    setPageState(readPage(currentParams));
  }, [currentParams]);

  React.useEffect(() => {
    if (syncedLocationRef.current !== null) {
      if (debouncedLocation === syncedLocationRef.current) {
        lastDebouncedLocationRef.current = debouncedLocation;
        syncedLocationRef.current = null;
      }
      return;
    }
    if (lastDebouncedLocationRef.current === debouncedLocation) return;
    lastDebouncedLocationRef.current = debouncedLocation;
    writeUrl({ location: debouncedLocation, page: 1 }, 'push');
  }, [debouncedLocation, writeUrl]);

  const setJobType = (type: JobType) => {
    setJobTypeState(type);
    setCategoryState('all');
    setRankIdState('');
    setVesselTypeIdState('');
    setShorePositionIdState('');
    setPageState(1);
  };

  const setCategory = (nextCategory: JobCategory) => {
    setCategoryState(nextCategory);
    setPageState(1);
    writeUrl({ category: nextCategory, page: 1 }, 'push');
  };

  const setLocation = (nextLocation: string) => {
    setLocationState(nextLocation);
    setPageState(1);
  };

  const setRankId = (nextRankId: string) => {
    if (!nextRankId) {
      writeUrl({ rankId: '', page: 1 }, 'push');
      setRankIdState('');
      setPageState(1);
      return;
    }
    setRankIdState(nextRankId);
    setPageState(1);
    writeUrl({ rankId: nextRankId, page: 1 }, 'push');
  };

  const setVesselTypeId = (nextVesselTypeId: string) => {
    if (!nextVesselTypeId) {
      writeUrl({ vesselTypeId: '', page: 1 }, 'push');
      setVesselTypeIdState('');
      setPageState(1);
      return;
    }
    setVesselTypeIdState(nextVesselTypeId);
    setPageState(1);
    writeUrl({ vesselTypeId: nextVesselTypeId, page: 1 }, 'push');
  };

  const setShorePositionId = (nextShorePositionId: string) => {
    if (!nextShorePositionId) {
      writeUrl({ shorePositionId: '', page: 1 }, 'push');
      setShorePositionIdState('');
      setPageState(1);
      return;
    }
    setShorePositionIdState(nextShorePositionId);
    setPageState(1);
    writeUrl({ shorePositionId: nextShorePositionId, page: 1 }, 'push');
  };

  const setPage = (nextPage: number) => {
    setPageState(nextPage);
    writeUrl({ page: nextPage }, 'push');
  };

  return {
    jobType,
    category,
    location,
    debouncedLocation,
    rankId,
    vesselTypeId,
    shorePositionId,
    page,
    setJobType,
    setCategory,
    setLocation,
    setRankId,
    setVesselTypeId,
    setShorePositionId,
    setPage,
  };
}
